import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { Card, CardContent, CircularProgress, Grid, Typography } from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import MenuBookTwoToneIcon from "@mui/icons-material/MenuBookTwoTone";
import FolderTwoToneIcon from "@mui/icons-material/FolderTwoTone";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import IndexApp from "../../Components/IndexApp";
import Layout from "../../Components/Layout";
import HomeHeader from "../../Components/Ui/HomeHeader";
import ClientSessionRepository, { ClientDashboardSummary } from "../../Repositories/ClientSessionRepository";
import DocumentRepository, { DocumentInfo } from "../../Repositories/DocumentRepository";
import ButtonField from "../../Components/Ui/ButtonField";
import NotificationsWidget from "../../Components/App/Home/NotificationsWidget";
import HelpCard from "../../Components/App/Home/HelpCard";
import { BrandContent, useTailwindBrand } from "../../Components/shared";
import { capitalizeFirstLetter } from "../../Utils/Function";
import Functions from "../../Helpers/Functions";
import { getActiveBrandConfig, getActivePrestataireBrandConfig, getActivePrestationBrandConfig } from "../../config/environment.config";

/**
 * Espace client : écran d'accueil du tableau de bord.
 * Prochaine séance, compteurs du mois (séances, montant, crédit d'impôt
 * estimé), derniers documents reçus et raccourcis vers les rubriques.
 */
const ClientHome = () => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const navigate = useNavigate();
    const client = useSelector((state: any) => state.user.currentUser?.client);

    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<ClientDashboardSummary | null>(null);
    const [lastDocuments, setLastDocuments] = useState<DocumentInfo[]>([]);

    useEffect(() => {
        let cancelled = false;
        if (!client?.id) return undefined;
        Promise.all([
            ClientSessionRepository.getSummary(client.id).catch(() => null),
            DocumentRepository.getClientDocuments(client.id).catch(() => ({ documents: [] as DocumentInfo[] })),
        ]).then(([summaryResponse, documentsResponse]) => {
            if (cancelled) return;
            setSummary(summaryResponse);
            setLastDocuments((documentsResponse?.documents ?? []).slice(0, 3));
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [client?.id]);

    const next = summary?.nextSession ?? null;
    const nextStart = next?.date ? moment(next.date) : null;

    // tableau de bord commun : URLs des rubriques client
    const dashboardBase = `/${getActiveBrandConfig()}/tableau-de-bord`;
    const coursesUrl = `${dashboardBase}/${getActivePrestationBrandConfig()}`;
    const documentsUrl = `${dashboardBase}/documents`;
    const settingsUrl = `${dashboardBase}/parametre`;
    const notificationsUrl = `${dashboardBase}/notifications`;

    const counterCard = (title: string, value: string, subtitle?: string) => (
        <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                <CardContent>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: brandConfig.fontFamily }}>
                        {title}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: brandConfig.colors.primary, fontFamily: brandConfig.fontFamily }}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: brandConfig.fontFamily }}>
                            {subtitle}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );

    return (
        <IndexApp
            navBar={{ index: 0 }}
            component={
                <Layout
                    header={
                        <HomeHeader
                            title="📊 Tableau de bord"
                            subtitle={`Bonjour ${capitalizeFirstLetter(client?.firstName ?? "")} 👋 Retrouvez vos séances, vos documents et vos informations.`}
                        />
                    }
                >
                    <div className="bg-white rounded-md p-4 space-y-6">
                {loading ? (
                    <div className="flex items-center gap-3 p-6">
                        <CircularProgress size={20} />
                        <BrandContent tag="p" children={<>Chargement de votre espace…</>} type="description" className={common.body} />
                    </div>
                ) : (
                    <>
                        {/* Avance immédiate non activée : invitation à finaliser */}
                        {!client?.idClientTiersPrestation && (
                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl p-4 border border-amber-200 bg-amber-50">
                                <div>
                                    <Typography variant="body1" fontWeight={700} sx={{ color: "#92400e", fontFamily: brandConfig.fontFamily }}>
                                        ⚡ Activez votre avance immédiate de crédit d'impôt
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#92400e", fontFamily: brandConfig.fontFamily }}>
                                        Ne payez que 50 % de vos {getActivePrestationBrandConfig("s")} : le crédit d'impôt est déduit à la source par l'URSSAF.
                                    </Typography>
                                </div>
                                <ButtonField
                                    label="Activer maintenant"
                                    variant="contained"
                                    className="font-bold"
                                    onClick={() => navigate(settingsUrl, { state: { step: 3 } })}
                                />
                            </div>
                        )}
                        {/* Prochaine séance */}
                        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: brandConfig.colors.primary }}>
                            <CardContent>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <EventAvailableOutlinedIcon sx={{ color: brandConfig.colors.primary, fontSize: 32 }} />
                                        <div>
                                            <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: brandConfig.fontFamily }}>
                                                {next ? `Votre prochaine séance` : `Aucune séance à venir`}
                                            </Typography>
                                            {next && nextStart ? (
                                                <>
                                                    <Typography variant="h6" fontWeight={700} sx={{ color: brandConfig.colors.primary, fontFamily: brandConfig.fontFamily }}>
                                                        {capitalizeFirstLetter(nextStart.format("dddd DD MMMM YYYY"))} à {nextStart.format("HH:mm")}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: brandConfig.fontFamily }}>
                                                        {next.course?.subject ?? ""}
                                                        {next.teachr ? `, avec ${capitalizeFirstLetter(next.teachr.firstName)} ${next.teachr.lastName?.toUpperCase() ?? ""}` : ""}
                                                        {next.childFirstName ? `, pour ${capitalizeFirstLetter(next.childFirstName)}` : ""}
                                                        {` (${Functions.renderDuration((next.durationMinutes ?? 0) / 30)})`}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography variant="body2" sx={{ fontFamily: brandConfig.fontFamily }}>
                                                    Vos prochaines séances déclarées par votre {getActivePrestataireBrandConfig()} apparaîtront ici.
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                    <ButtonField
                                        label={`Voir mes ${getActivePrestationBrandConfig("s")}`}
                                        variant="outlined"
                                        onClick={() => navigate(coursesUrl)}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Nouveautés : dernières notifications non lues (store partagé avec la cloche) */}
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <div className="flex items-center justify-between mb-2">
                                    <Typography variant="h6" fontWeight={700} sx={{ color: brandConfig.colors.primary, fontFamily: brandConfig.fontFamily }}>
                                        🔔 Nouveautés
                                    </Typography>
                                    <ButtonField label="Tout voir" variant="text" onClick={() => navigate(notificationsUrl)} />
                                </div>
                                <NotificationsWidget showAllLink={false} />
                            </CardContent>
                        </Card>

                        {/* Aide et contact : mail, téléphone et formulaire vers l'équipe */}
                        <HelpCard />

                        {/* Compteurs du mois */}
                        <Grid container spacing={2}>
                            {counterCard(
                                `Séances en ${moment().format("MMMM")}`,
                                `${summary?.month?.sessionsCount ?? 0}`,
                                summary?.month?.totalMinutes ? Functions.renderDuration(summary.month.totalMinutes / 30) : "0h"
                            )}
                            {counterCard(
                                "Montant du mois",
                                `${(summary?.month?.totalAmount ?? 0).toFixed(2)} €`,
                                "Total TTC des séances du mois"
                            )}
                            {counterCard(
                                "Crédit d'impôt estimé",
                                `${(summary?.month?.estimatedTaxCredit ?? 0).toFixed(2)} €`,
                                "Avance immédiate, 50 % des prestations éligibles"
                            )}
                        </Grid>

                        {/* Derniers documents */}
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <div className="flex items-center justify-between mb-2">
                                    <Typography variant="h6" fontWeight={700} sx={{ color: brandConfig.colors.primary, fontFamily: brandConfig.fontFamily }}>
                                        Derniers documents
                                    </Typography>
                                    <ButtonField label="Tout voir" variant="text" onClick={() => navigate(documentsUrl)} />
                                </div>
                                {lastDocuments.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: brandConfig.fontFamily }}>
                                        Vos devis, factures et attestations apparaîtront ici.
                                    </Typography>
                                ) : (
                                    <ul className="space-y-2">
                                        {lastDocuments.map((doc, index) => (
                                            <li key={index} className="flex items-center justify-between gap-3">
                                                <Typography variant="body2" sx={{ fontFamily: brandConfig.fontFamily }}>
                                                    {doc.label}
                                                    {doc.date ? `, ${moment(doc.date).format("DD MMM YYYY")}` : ""}
                                                </Typography>
                                                <ButtonField label="Ouvrir" variant="text" onClick={() => window.open(doc.url, "_blank", "noopener")} />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>

                        {/* Raccourcis */}
                        <div className="flex flex-wrap gap-3">
                            <ButtonField
                                label={`Mes ${getActivePrestationBrandConfig("s")}`}
                                variant="contained"
                                startIcon={<MenuBookTwoToneIcon />}
                                onClick={() => navigate(coursesUrl)}
                            />
                            <ButtonField
                                label="Mes documents"
                                variant="outlined"
                                startIcon={<FolderTwoToneIcon />}
                                onClick={() => navigate(documentsUrl)}
                            />
                            <ButtonField
                                label="Paramètres"
                                variant="outlined"
                                startIcon={<SettingsTwoToneIcon />}
                                onClick={() => navigate(settingsUrl)}
                            />
                        </div>
                    </>
                )}
                    </div>
                </Layout>
            }
        />
    );
};

export default ClientHome;
